import { addDays, format, isBefore, isValid, parseISO, subDays } from "date-fns";
import { enUS } from "date-fns/locale";

/**
 * Formats a future date by adding specified days to the current date
 *
 * @param days - Number of days to add to current date
 * @returns Formatted date string (e.g., "January 15, 2024")
 *
 * @example
 * ```typescript
 * getFutureDate(7); // Returns date 7 days from now
 * ```
 */
export const getFutureDate = (days: number) => {
    return format(addDays(new Date(), days), "MMMM d, yyyy", {
        locale: enUS,
    });
};

/**
 * Gets the current date formatted as a string
 *
 * @returns Current date formatted as "MMMM d, yyyy" (e.g., "January 15, 2024")
 *
 * @example
 * ```typescript
 * currentDate(); // Returns "January 15, 2024"
 * ```
 */
export const currentDate = () =>
    format(new Date(), "MMMM d, yyyy", { locale: enUS });

/**
 * Checks if a date is within the last N days from today (not older than N days)
 *
 * @param expectedDate - The date to check (can be string, Date, null, or undefined)
 * @param till - Number of days to check back from today
 * @returns true if the date is within the last N days (including today and future dates), false otherwise
 *
 * @example
 * ```typescript
 * // Check if date is within last 5 days
 * isWithinDate(new Date(), 5); // true (today)
 * isWithinDate(new Date(Date.now() - 3 * 24 * 60 * 60 * 1000), 5); // true (3 days ago)
 * isWithinDate(new Date(Date.now() - 10 * 24 * 60 * 60 * 1000), 5); // false (10 days ago)
 * ```
 */
export const isWithinDate = (
    expectedDate: string | Date | null | undefined,
    till: number
): boolean => {
    if (!expectedDate) return false;

    const date =
        expectedDate instanceof Date ? expectedDate : new Date(expectedDate);
    if (isNaN(date.getTime())) return false;

    const limit = subDays(new Date(), till);
    return !isBefore(date, limit);
};

/**
 * Formats a date range string for expected delivery dates.
 * Returns a range from 7 to 14 days after the given date.
 *
 * @param curDate - The starting date to calculate the range from
 * @returns Formatted date range string
 *   - Same month: "Aug 7–13" (only day shown for end date)
 *   - Different months: "Aug 28 – Sep 4" (month and day for both)
 *
 * @example
 * ```typescript
 * // Same month
 * getDateRangeString(new Date("2024-08-01")); // "Aug 8 – 15"
 *
 * // Different months
 * getDateRangeString(new Date("2024-08-21")); // "Aug 28 – Sep 4"
 * ```
 */
export function getDateRangeString(curDate: Date): string {
    const start = addDays(curDate, 7);
    const end = addDays(curDate, 14);

    const sameMonth = format(start, "MMM") === format(end, "MMM");

    if (sameMonth) {
        // Aug 7–13
        return `${format(start, "MMM d")} – ${format(end, "d")}`;
    } else {
        // Aug 28 – Sep 4
        return `${format(start, "MMM d")} – ${format(end, "MMM d")}`;
    }
}

/**
 * Formats an ISO date string (`YYYY-MM-DD` or `YYYY-MM-DDTHH:mm:ss.sssZ`)
 * into `MM/DD/YYYY` **without timezone conversion**.
 *
 * Intended for calendar dates (e.g. date of birth).
 */
export const formatIsoDateWithoutTZ = (iso?: string): string => {
    if (!iso) return "";

    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
    if (!match) return "";

    const [, year, month, day] = match;
    return `${month}/${day}/${year}`;
};

export const toDate = (value: Date | string | null | undefined): Date | null => {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

// Like `toDate`, but a date-only value ("2026-10-22") is that day in the reader's time zone. `new Date`
// reads it as UTC midnight, which is the evening before for anyone west of Greenwich.
export const toCalendarDate = (value: Date | string | null | undefined): Date | null => {
    if (typeof value !== "string" || !DATE_ONLY_PATTERN.test(value)) return toDate(value);

    // A well-formed value can still name a day that doesn't exist, such as 2026-02-30.
    const day = parseISO(value);
    return isValid(day) ? day : null;
};
